import React, { useEffect, useState } from 'react';
import { Play, Pause, SkipBack, Volume2 } from 'lucide-react';
import { Poem } from '../../types';
import { playNote } from '../../utils/audioUtils';
type SingAlongProps = {
  poem: Poem;
};
// Simple melody patterns for demonstration
const MELODY_PATTERNS: Record<string, string[]> = {
  '4/4': ['1', '2', '3', '5', '5', '3', '2', '1'],
  '6/8': ['1', '3', '5', '5', '3', '1'],
  '3/4': ['1', '2', '3', '3', '2', '1']
};
export function SingAlong({
  poem
}: SingAlongProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [showPinyin, setShowPinyin] = useState(true);
  const melody = MELODY_PATTERNS[poem.timeSignature] || MELODY_PATTERNS['4/4'];
  useEffect(() => {
    let interval: number;
    if (isPlaying) {
      interval = window.setInterval(() => {
        const line = poem.content[currentLine];
        if (currentChar < line.length - 1) {
          setCurrentChar(prev => prev + 1);
          playNote(melody[currentChar % melody.length]);
        } else if (currentLine < poem.content.length - 1) {
          setCurrentLine(prev => prev + 1);
          setCurrentChar(0);
          playNote(melody[0]);
        } else {
          setIsPlaying(false);
          setCurrentLine(0);
          setCurrentChar(0);
        }
      }, 600);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentLine, currentChar, poem.content, melody]);
  const reset = () => {
    setIsPlaying(false);
    setCurrentLine(0);
    setCurrentChar(0);
  };
  return <div className="flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-172">
      {/* Lyrics display */}
      <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-8 w-full mb-8 min-h-[300px] flex flex-col items-center justify-center relative overflow-hidden" data-id="element-173">
        {/* Decorative elements */}
        <div className="absolute top-4 left-4 w-8 h-8 bg-white/20 rounded-full" data-id="element-174" />
        <div className="absolute top-8 right-8 w-6 h-6 bg-white/20 rounded-full" data-id="element-175" />
        <div className="absolute bottom-12 left-12 w-4 h-4 bg-white/20 rounded-full" data-id="element-176" />

        {/* Current lyrics */}
        <div className="text-center z-10" data-id="element-177">
          {poem.content.map((line, lineIndex) => <div key={lineIndex} className={`mb-4 transition-all duration-300 ${lineIndex === currentLine ? 'scale-110' : lineIndex < currentLine ? 'opacity-40 scale-90' : 'opacity-60'}`} data-id="element-178">
              <div className="flex justify-center gap-2" data-id="element-179">
                {line.split('').map((char, charIndex) => <span key={charIndex} className={`text-3xl md:text-4xl font-bold transition-all duration-200 ${lineIndex === currentLine && charIndex <= currentChar ? 'text-yellow-300 scale-110' : 'text-white'}`} data-id="element-180">
                    {char}
                  </span>)}
              </div>
            </div>)}
        </div>

        {/* Progress indicator */}
        <div className="absolute bottom-4 left-4 right-4" data-id="element-181">
          <div className="h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-182">
            <div className="h-full bg-yellow-400 transition-all duration-300" style={{
            width: `${(currentLine * 100 + (currentChar / poem.content[currentLine]?.length || 1) * 100) / poem.content.length}%`
          }} data-id="element-183" />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 mb-6" data-id="element-184">
        <button onClick={reset} className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-185">
          <SkipBack className="w-6 h-6 text-gray-700" data-id="element-186" />
        </button>

        <button onClick={() => setIsPlaying(!isPlaying)} className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${isPlaying ? 'bg-gray-200' : 'bg-cyan-500'}`} data-id="element-187">
          {isPlaying ? <Pause className={`w-8 h-8 ${isPlaying ? 'text-gray-700' : 'text-white'}`} data-id="element-188" /> : <Play className="w-8 h-8 text-white ml-1" data-id="element-189" />}
        </button>

        <button onClick={() => setShowPinyin(!showPinyin)} className={`p-3 rounded-full transition-colors ${showPinyin ? 'bg-cyan-100 text-cyan-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-190">
          <Volume2 className="w-6 h-6" data-id="element-191" />
        </button>
      </div>

      {/* Tips */}
      <div className="bg-cyan-50 rounded-xl p-4 max-w-md text-center" data-id="element-192">
        <p className="text-cyan-800 font-medium" data-id="element-193">🎤 跟着高亮的字一起唱吧！</p>
        <p className="text-cyan-600 text-sm mt-1" data-id="element-194">
          每个字都配有简单的旋律，大声唱出来~
        </p>
      </div>
    </div>;
}